import * as R from 'ramda'
import { stripOutHtml } from '../helpers'
import { getQuestionTitle } from '../../utils/QuestionHelper'
import { SERIES_PALETTE } from './chartRenderSettings'

class DataFormatter {
    constructor(appStore, productNames, portfolioNames, divisionNames, dashStore, tagNames) {
        this.appStore = appStore
        this.productNames = productNames
        this.portfolioNames = portfolioNames
        this.divisionNames = divisionNames
        this.dashStore = dashStore
        this.tagNames = tagNames
    }

    sortBy = (arr, key, direction = 1) => {
        if (!key || key === 'none') return arr
        return arr.sort((a, b) => {
            let value = 0
            const valueA = !isNaN(Number(a[key])) ? Number(a[key]) : a[key]
            const valueB = !isNaN(Number(b[key])) ? Number(b[key]) : b[key]
            if (valueA < valueB) {
                value = -1
            } else if (valueA > valueB) {
                value = 1
            }
            return value * direction
        })
    }

    convertCurrency = (value, exchangeRateDate, element) => {
        const { currencyConversions, todaysCurrencyRates } = this.dashStore
        const decimals = R.pathOr(element.chartType === 'gauge0-5' ? 2 : 0, ['widgetRenderSettings', 'dataDecimals'], element)
        const setDecimals = ['text', 'multiText', 'gauge', 'gauge0-5', 'lmx'].includes(element.chartType)
        const widgetCurrency = R.pathOr(element.isCurrency ? 'USD' : undefined, ['widgetRenderSettings', 'currency'], element)
        if (!widgetCurrency) return setDecimals && typeof value === 'number' ? value.toFixed(decimals) : value
        const exchangeRate = R.pathOr(
            R.pathOr(1, [widgetCurrency], todaysCurrencyRates),
            [exchangeRateDate, widgetCurrency],
            currencyConversions,
        )
        return setDecimals ? (value * exchangeRate).toFixed(decimals) : value * exchangeRate
    }

    removeProductTag = (text) => {
        const textArr = text.split('-')
        textArr.pop()
        return textArr.join('-')
    }

    getItemName = (element, portfolioId, productId, divisionId, year, multiName, tagId) => {
        const {
            productNames, portfolioNames, divisionNames, tagNames,
        } = this
        const { appStore } = this
        let name = ''
        if (element.scope === 'client') name = appStore.userData.clientName
        else if (element.scope === 'portfolio') name = portfolioNames[portfolioId]
        else if (element.scope === 'product') name = productNames[productId]
        else if (element.scope === 'division') name = divisionNames[divisionId]
        const yearName = year && year >= 1900 ? ` - ${year}` : ''
        const multi = multiName ? ` - ${multiName}` : ''
        const tag = tagId ? ` - ${tagNames[tagId]}` : ''
        return `${name}${yearName}${multi}${tag}`
            .replace('&amp;', '')
    }

    formatYearName = (year) => {
        if (year >= 1900) return year
        if (Number(year) === 0 || Number(year) === 84) return 'All Time'
        return `${year} Months`
    }

    formatPriorityData = (data, element) => {
        return data.map(({ year, data }) => ({
            data: element.options.map(([k, v]) => ({ name: v, value: data[k] ?? 0 })),
            name: year,
        }))
    }

    formatAvgData = (unsortedData, element) => {
        const data = unsortedData.sort((a, b) => a.year - b.year)
        if (['multiQuestion', 'quadrantAnalysis'].includes(element.kpiType) && element.chartType !== 'multiText') {
            const yData = data.reduce((obj, d) => {
                const value = obj[d.multiName || element.title] || []
                value.push({
                    name: 'BONGO',
                    division: d.divisionId,
                    portfolio: d.portfolioId,
                    product: d.productId,
                    multiName: d.multiName,
                    value: this.convertCurrency(R.pathOr(d.data, ['data', d.multiQuestionId], d), d.exchangeRateDate, element),
                    year: d.year,
                    endDate: d.endDate,
                    benchmarkName: d.benchmarkName,
                    benchmark: d.benchmark,
                    tagOptionId: d.tagOptionId,
                })
                return { ...obj, [d.multiName || element.title]: value }
            }, {})
            return Object.keys(yData).map((y) => ({
                data: yData[y].map((v) => ({
                    value: Number(v.value),
                    name: v.benchmarkName || this.getItemName(element, v.portfolio, v.product, v.division, v.year, undefined, v.tagOptionId),
                    endDate: v.endDate,
                    benchmark: v.benchmark,
                    benchmarkName: v.benchmarkName,
                    productId: v.product,
                    portfolioId: v.portfolio,
                    divisionId: v.division,
                    tagOptionId: v.tagOptionId,
                })),
                name: y || element.title,
            }))
        }
        if (['YOY', '', 'ROL', 'ROL-36', 'ROL-84', 'ALL'].includes(element.benchmarkType) && ['bar', 'column', 'line'].includes(element.chartType)) {
            const yData = data.reduce((obj, d) => {
                const value = obj[d.year] || []
                value.push({
                    name: `${d.divisionId}_${d.portfolioId}_${d.productId}${d.multiName ? `_${d.multiName}` : ''}`,
                    division: d.divisionId,
                    portfolio: d.portfolioId,
                    product: d.productId,
                    multiName: d.multiName,
                    value: this.convertCurrency(d.data, d.exchangeRateDate, element),
                    endDate: d.endDate,
                    benchmarkName: d.benchmarkName,
                    benchmark: d.benchmark,
                    productId: d.productId,
                    tagOptionId: d.tagOptionId,
                })
                return { ...obj, [d.year]: value }
            }, {})

            return Object.keys(yData).map((y) => ({
                data: yData[y].map((v) => ({
                    value: Number(v.value),
                    name: v.benchmarkName || this.getItemName(element, v.portfolio, v.product, v.division, undefined, v.multiName, v.tagOptionId),
                    year: v.year,
                    endDate: v.endDate,
                    benchmark: v.benchmark,
                    benchmarkName: v.benchmarkName,
                    productId: v.product,
                    tagOptionId: v.tagOptionId,
                })),
                name: this.formatYearName(y),
            }))
        }

        return data.map((d) => ({
            value: Number(this.convertCurrency(R.pathOr(d.data, ['data', d.multiQuestionId], d), d.exchangeRateDate, element)),
            name: d.benchmarkName || this.getItemName(
                element, d.portfolioId, d.productId, d.divisionId, d.year, d.multiName, d.tagOptionId,
            ),
            year: d.year,
            endDate: d.endDate,
            benchmarkName: d.benchmarkName,
            benchmark: d.benchmark,
            productId: d.productId,
            tagOptionId: d.tagOptionId,
        }))
    }

    formatPctData = (unsortedData, element) => {
        const data = unsortedData.sort((a, b) => a.year - b.year)
        const { options } = element
        const optionFilters = R.pathOr([], ['optionFilter'], element).map((f) => f.value)
        const filteredOptions = optionFilters.length
            ? options.filter((o) => optionFilters.includes(o[0]))
            : options
        const names = filteredOptions?.reduce((n, c) => ({ ...n, [R.pathOr('', [0], c)]: stripOutHtml(R.pathOr('', [1], c)) }), {})
        const isBarType = ['bar', 'column', 'line', 'standardBar', 'standardColumn', 'inheritedStackedBar', 'npsChart', 'kpiChart', 'pie', 'doughnut']
            .includes(element.chartType)
        return data.map((d) => ({
            data: filteredOptions?.map(
                ([k]) => ({
                    value: Number(this.convertCurrency(
                        d.data[k], d.exchangeRateDate, element,
                    )),
                    name: names[k],
                    reportingValue: R.pathOr(undefined, ['optionReportingValues', String(k).toLowerCase()], element),
                    tagOptionId: d.tagOptionId,
                }),
            ),
            name: d.benchmarkName || (isBarType
                ? this.formatYearName(d.year)
                : this.getItemName(
                    element, d.portfolioId, d.productId, d.divisionId, d.year, d.multiName, d.tagOptionId,
                )),
            year: d.year,
            endDate: d.endDate,
            benchmarkName: d.benchmarkName,
            benchmark: d.benchmark,
            productId: d.productId,
            tagOptionId: d.tagOptionId,
            tagOptionName: d.tagOptionId ? this.tagNames[d.tagOptionId] : undefined,
        }))
    }

    formatNFOption = (unsortedData, element) => {
        const data = unsortedData.sort((a, b) => a.year - b.year)
        const names = element.options.reduce((n, c) => (
            c[0] === element.questionId ? { ...n, [c[0]]: stripOutHtml(c[1]) } : n
        ), {})

        if (['YOY', '', 'ROL', 'ROL-36', 'ROL-84', 'ALL'].includes(element.benchmarkType) && ['bar', 'column', 'line'].includes(element.chartType)) {
            return data.map((d) => ({
                data: [{
                    value: Number(this.convertCurrency(
                        d.data[element.questionId],
                        d.exchangeRateDate,
                        element,
                    )),
                    name: names[element.questionId],
                }],
                name: d.benchmarkName || this.formatYearName(d.year),
                year: d.year,
                benchmarkName: d.benchmarkName,
                benchmark: d.benchmark,
                productId: d.productId,
                tagOptionId: d.tagOptionId,
            }))
        }
        return data.map((d) => ({
            value: Number(this.convertCurrency(
                d.data[element.questionId],
                d.exchangeRateDate,
                element,
            )),
            name: d.benchmarkName || this.getItemName(
                element, d.portfolioId, d.productId, d.divisionId, d.year, d.multiName, d.tagOptionId,
            ),
            year: d.year,
            benchmarkName: d.benchmarkName,
            benchmark: d.benchmark,
            productId: d.productId,
            tagOptionId: d.tagOptionId,
        }))
    }

    getTBOptionName = (o, element) => {
        const { langcode } = this.appStore
        return R.pathOr('', ['text'], getQuestionTitle(
            {
                content: R.pathOr({}, ['langs'], o),
                // Prefer the survey's default locale so option/row labels render in
                // the survey's language rather than the viewer's UI language.
                locale: element.surveyDefaultLocale || langcode,
                defaultLocale: element.questionInitialLocale,
                initialLocale: 'en_GB',
            },
        ))
    }

    formatTBData = (unsortedData, element, surveyYear) => {
        const { questionId, kpiType, chartType, settings } = element
        // only current year is required for TB data
        const data = unsortedData.filter((d) => !!d.data && d.year === surveyYear)

        const tbColumns = R.pathOr([], ['columns'], element)

        const optionFilters = R.pathOr([], ['optionFilter'], element).map((f) => f.value)
        const filteredTbColumns = (optionFilters.length
            ? tbColumns.filter((o) => optionFilters.includes(o[0]))
            : tbColumns)
            .filter(([cId]) => (
                !settings?.excludeNonNumeric
                || R.pathOr(undefined, ['optionReportingValues', String(cId).toLowerCase()], element) != null
            ))
        const names = element.questionOptions.reduce((obj, o) => (
            { ...obj, [o.id]: stripOutHtml(this.getTBOptionName(o, element)) }
        ), {})
        const options = element.questionTypeCode === 'TB - row' ? { [questionId]: names[questionId] } : names
        if (['doughnut', 'pie', 'treemap'].includes(chartType)) {
            return filteredTbColumns.map(([cId, cName]) => ({
                name: cName,
                value: this.convertCurrency(
                    data[0].data[`${questionId}_${cId}`],
                    data[0].data.exchangeRateDate,
                    element,
                ),
            }))
        }

        const isBarTypeTB = ['bar', 'column', 'line', 'standardBar', 'standardColumn', 'npsChart']
            .includes(chartType)

        // A matrix showing its averages plots each row's AVERAGE score rather than its
        // column breakdown, so the series are the selected years (comparison years plus
        // the current year) and the x axis stays as the matrix rows. Values are rounded
        // to 2dp to match the score columns shown alongside the stacked charts. Only the
        // column/bar charts render this shape - see chartTypeOptions.
        if (element.questionTypeCode === 'TB'
            && settings?.showAverage
            && ['standardColumn', 'standardBar'].includes(chartType)) {
            const rowAverage = (year, optionId) => {
                const yearAverages = R.pathOr({}, ['averages', String(year)], element)
                const raw = yearAverages[optionId]
                    ?? yearAverages[String(optionId).toUpperCase()]
                    ?? yearAverages[String(optionId).toLowerCase()]
                const value = Number(raw)
                return Number.isFinite(value) ? Number(value.toFixed(2)) : 0
            }

            return unsortedData
                .filter((d) => !!d.data && !d.benchmarkName && Number.isFinite(Number(d.year)))
                .sort((a, b) => Number(a.year) - Number(b.year))
                .map((d) => ({
                    name: this.formatYearName(d.year),
                    year: d.year,
                    productId: d.productId,
                    data: Object.keys(options).map((optionId) => {
                        const value = rowAverage(d.year, optionId)
                        return {
                            name: names[optionId],
                            value,
                            // `avg` is what the sorting in the presentation store reads for TB.
                            avg: value,
                            year: d.year,
                            endDate: d.endDate,
                            productId: d.productId,
                        }
                    }),
                }))
        }

        if (kpiType === 'avg') {
            return data.map((d) => ({
                data: Object.keys(options).map((optionId) => ({
                    name: `${names[optionId]}`,
                    value: Number(this.convertCurrency(d.data[optionId], d.exchangeRateDate, element)),
                    year: d.year,
                    endDate: d.endDate,
                })),
                name: d.benchmarkName || ((isBarTypeTB || settings.showAverage)
                    ? this.formatYearName(d.year)
                    : this.getItemName(
                        element, d.portfolioId, d.productId, d.divisionId, d.year, d.multiName, d.tagOptionId,
                    )),
                benchmarkName: d.benchmarkName,
                benchmark: d.benchmark,
                productId: d.productId,
            }))
        }

        return filteredTbColumns.map(([cId, cName]) => ({
            name: cName,
            reportingValue: R.pathOr(undefined, ['optionReportingValues', String(cId).toLowerCase()], element),
            data: data.map((d) => Object.keys(options).map((optionId) => ({
                name: d.benchmarkName || names[optionId],
                value: this.convertCurrency(
                    d.data[`${optionId}_${cId}`],
                    d.exchangeRateDate,
                    element,
                ),
                year: d.year,
                avg: element.averages?.[d.year]?.[optionId],
                endDate: d.endDate,
                benchmarkName: d.benchmarkName,
                benchmark: d.benchmark,
                productId: d.productId,
            }))).flat(1),
        }))
    }

    groupXAxisLabels = (labels, removeZeroNull, element) => Object.values(labels.reduce((obj, l) => {
        // Accumulate against the SAME key we write to so a label that appears across multiple
        // series (e.g. stacked segments / multiple columns) sums into a single total. Reading
        // [l.name] here never matched the `${l.name}-${l.productId}` write key, so values were
        // overwritten and a label was dropped whenever its LAST series happened to be zero.
        const key = `${l.name}-${l.productId}`
        const oldValue = !isNaN(Number(R.pathOr(0, [key, 'value'], obj))) ? Number(R.pathOr(0, [key, 'value'], obj)) : 0
        const newValue = R.pathOr([{ value: l.value }], ['data'], l).reduce((total, v) => total + Number(v.value), oldValue)
        return {
            ...obj,
            [key]: {
                name: `${l.name}-${element.scope === 'product' && l.productId ? l.productId : ''}`,
                value: !isNaN(newValue) ? newValue : 0,
                endDate: new Date(l.endDate) > R.pathOr(0, [key, 'endDate'], obj)
                    ? new Date(l.endDate)
                    : R.pathOr(0, [key, 'endDate'], obj),
                year: l.year,
                isNull: isNaN(newValue),
                tagOptionName: l.tagOptionId ? this.tagNames[l.tagOptionId] : undefined,
            },
        }
    }, {})).filter((d) => (
        (!['zero', 'both', 'null'].includes(removeZeroNull))
            || (removeZeroNull === 'zero' && !(d.value === 0 && !d.isNull))
            || (removeZeroNull === 'null' && !d.isNull)
            || (removeZeroNull === 'both' && !d.isNull && d.value !== 0)
    ))

    getXAxisLabels = (element, fData) => {
        const {
            chartType, multiQuestion, dataDetails, kpiType,
        } = element
        const { type } = dataDetails
        const { oId } = this.dashStore
        const { sortBy, groupXAxisLabels } = this

        const defaultSortKey = oId !== 6 && element.scope === 'product' ? 'endDate' : 'none'
        const sortByKey = R.pathOr(defaultSortKey, ['widgetRenderSettings', 'sortBy'], element)
        const sortDirection = R.pathOr(1, ['widgetRenderSettings', 'sortDirection'], element)
        const removeZeroNull = R.pathOr(undefined, ['widgetRenderSettings', 'removeZeroNull'], element)
        if (chartType === 'quadrantAnalysis') {
            return {
                x: `${R.pathOr(undefined, [0, 'label'], multiQuestion)} - ${R.pathOr('', [0, 'group'], multiQuestion)}`,
                y: `${R.pathOr(undefined, [1, 'label'], multiQuestion)} - ${R.pathOr('', [1, 'group'], multiQuestion)}`,
                z: `${R.pathOr(undefined, [2, 'label'], multiQuestion)} - ${R.pathOr('', [2, 'group'], multiQuestion)}`,
            }
        }

        if ((['column', 'line', 'bar'].includes(chartType) && ['YOY', '', 'ROL', 'ROL-36', 'ROL-84', 'ALL'].includes(element.benchmarkType))
        || (['multiQuestion', 'quadrantAnalysis'].includes(kpiType) && element.chartType !== 'multiText')
        || ['TB', 'TB - row'].includes(element.questionTypeCode)) {
            return !['pct', 'quadrantAnalysis'].includes(type)
                ? [...new Set(
                    sortBy(
                        groupXAxisLabels(fData.map(((d) => d.data.map((nd) => nd))).flat(2), removeZeroNull, element),
                        sortByKey,
                        sortDirection,
                    )
                        .map((d) => d.name),
                )]
                : sortBy(
                    groupXAxisLabels(fData.map((d) => d.data).flat(), removeZeroNull, element),
                    sortByKey,
                    sortDirection,
                ).map((d) => d.name)
        }

        if (['stackedColumn', 'stackedLine', 'stackedBar'].includes(chartType)
         || (['column', 'line', 'bar'].includes(chartType) && !['YOY', '', 'ROL', 'ROL-36', 'ROL-84', 'ALL'].includes(element.benchmarkType))) {
            return sortBy(groupXAxisLabels(fData, removeZeroNull, element), sortByKey, sortDirection).map((d) => d.name)
        }

        // console.log('SSS', Array.from(new Set(fData.map((d) => d.data.map((dat) => dat.name)).flat())))
        if (['npsChart', 'standardBar', 'standardColumn', 'inheritedStackedBar', 'kpiChart', 'pie', 'doughnut'].includes(chartType)) {
            if (sortByKey === 'value') {
                fData[0].data.sort((a, b) => b.value - a.value)
            }

            return sortBy(
                Array.from(
                    new Set(
                        fData
                            .map(
                                (d) => ['avg', 'nps'].includes(element.responseType) ? d.name : d.data
                                    .map((dat) => `${dat.name}`),
                            )
                            .flat(),
                    ),
                ).map((i) => ({ name: i })), sortByKey, sortDirection,
            ).map((d) => d.name)
        }

        return []
    }

    formatBarColumnData = (fData, xAxisLabels, element) => {
        const { benchmarkType, questionTypeCode, dataDetails } = element
        const { type } = dataDetails
        if (['TB', 'TB - row'].includes(questionTypeCode)) {
            return fData.map((s) => {
                const optionData = s.data.reduce((obj, o) => ({ ...obj, [`${o.name}-${element.scope === 'product' && o.productId ? o.productId : ''}`]: o }), {})
                const data = xAxisLabels.map((l) => optionData[l])
                // Bar charts take their categories bottom-up, so the series data is
                // reversed to stay aligned with the labels - as the generic branch below.
                return { ...s, data: element.chartType === 'standardBar' ? data.reverse() : data }
            })
        }
        if (!['YOY', '', 'ROL', 'ROL-36', 'ROL-84', 'ALL'].includes(benchmarkType) && !['multiQuestion', 'quadrantAnalysis'].includes(element.kpiType)) {
            const labelData = fData
                .reduce((obj, v) => ({ ...obj, [`${v.name}`]: v }), {})
            return ['pct'].includes(type)
                ? R.pathOr([], [0, 'data'], fData).map((c, i) => (
                    {
                        data: xAxisLabels.map((d) => ({ ...labelData[d].data[i], name: d.name })),
                        name: c.name,
                    }
                ))
                : [{
                    name: element.title,
                    data: xAxisLabels.map((d) => ({
                        value: labelData[d].value,
                        benchmark: labelData[d].benchmark,
                        benchmarkName: labelData[d].benchmark,
                        name: labelData[d].name,
                        tagOptionId: labelData[d].tagOptionId,
                        tagOptionName: labelData[d].tagOptionId ? this.tagNames[labelData[d].tagOptionId] : undefined,
                    })),
                }]
        }

        if (element.responseType === 'avg') {
            return [{
                name: '',
                data: fData,
            }]
        }

        if (element.responseType === 'OSS') {
            const baseData = [
                {
                    data: Array.from({ length: 100 }, (_, i) => ({ value: i + 1, itemStyle: { color: '#d6d6d6' }, label: { show: false }, emphasis: { disabled: true }})),
                    name: '',
                    benchmarkName: '',
                    benchmark: '',
                    tagOptionId: '',
                    tagOptionName: undefined,
                },
            ]

            fData.forEach((d, i) => {
                // Every year is a full 100-point series with a single non-zero bar at
                // its score, so labels are off everywhere except that one point.
                const data = Array.from({ length: 100 }, () => ({ value: 0, label: { show: false } }))
                const val = Math.round(d.value)
                // Bars take their colour from the shared series palette by chart
                // position - index 0 is the grey 1-100 scale, so years start at 1.
                // The label pill reuses that colour to match its bar.
                const colour = SERIES_PALETTE[(i + 1) % SERIES_PALETTE.length]
                data[val - 1] = {
                    value: Math.min(val + 5, 100),
                    label: {
                        show: true,
                        position: 'top',
                        distance: 0,
                        formatter: `${d.year}\n${val}`,
                        align: 'center',
                        verticalAlign: 'middle',
                        color: '#fff',
                        fontSize: 22,
                        fontWeight: 500,
                        lineHeight: 30,
                        backgroundColor: colour,
                        padding: [10, 14],
                        borderRadius: 2,
                    },
                }

                baseData.push({
                    data,
                    name: `${d.year}`,
                    benchmarkName: d.benchmarkName,
                    benchmark: d.benchmark,
                    tagOptionId: d.tagOptionId,
                    tagOptionName: d.tagOptionId ? this.tagNames[d.tagOptionId] : undefined,
                })
            })

            return baseData
        }
        return fData.map((d) => {
            // const labelData = d.data
            //     .reduce((obj, v) => ({ ...obj, [`${v.name}-${element.scope === 'product' && v.productId ? v.productId : ''}`]: v }), {})
            let data = []
            if (['nps', 'avg'].includes(element.responseType)) {
                xAxisLabels.forEach((l) => {
                    if (l === d.name) {
                        data.push(d)
                    }
                })
            } else {
                data = xAxisLabels
                    .map((l) => d.data.find((da) => `${da.name}` === l) || { value: null })
            }

            return {
                data: element.chartType === 'standardBar' ? data.reverse() : data,
                name: d.name,
                year: d.year,
                benchmarkName: d.benchmarkName,
                benchmark: d.benchmark,
                tagOptionId: d.tagOptionId,
                tagOptionName: d.tagOptionId ? this.tagNames[d.tagOptionId] : undefined,
            }
        })
    }

    formatGauge = (fData) => (
        fData.map((data) => R.mergeLeft(data, R.pathOr({}, ['data', 0], data)))
    )

    formatStackedData = (fData, element, xAxisLabels) => {
        const { questionTypeCode, dataDetails } = element
        const { type } = dataDetails
        if (['multiQuestion', 'quadrantAnalysis'].includes(element.kpiType)) {
            return fData.map((d) => {
                const labelData = d.data
                    .reduce((obj, v) => ({ ...obj, [`${v.name}-${element.scope === 'product' && v.productId ? v.productId : ''}`]: v }), {})
                return {
                    data: xAxisLabels.map((l) => labelData[l] || 0),
                    name: d.name,
                }
            })
        }

        if (['TB', 'TB - row'].includes(questionTypeCode)) {
            return fData.map((s) => {
                const optionData = s.data.reduce((obj, o) => ({ ...obj, [`${o.name}-${element.scope === 'product' && o.productId ? o.productId : ''}`]: o }), {})
                return { ...s, data: xAxisLabels.map((l) => optionData[l]) }
            })
        }

        const labelData = fData
            .reduce((obj, v) => ({ ...obj, [`${v.name}-${element.scope === 'product' && v.productId ? v.productId : ''}`]: v }), {})
        return ['pct'].includes(type)
            ? R.pathOr([], [0, 'data'], fData).map((c, i) => (
                {
                    data: xAxisLabels.map((d) => ({ ...labelData[d].data[i], name: d.name })),
                    name: c.name,
                    reportingValue: c.reportingValue,
                }
            ))
            : [{ name: element.title, data: xAxisLabels.map((d) => labelData[d].value) }]
    }

    getQuadrantSeriesName = (seriesName, records, index) => {
        switch (seriesName) {
            case 'division': {
                return String(R.pathOr('undefined', [R.pathOr(undefined, ['xAxis', index, 'divisionId'], records)], this.divisionNames))
            }
            case 'portfolio': {
                return String(R.pathOr('undefined', [R.pathOr(undefined, ['xAxis', index, 'portfolioId'], records)], this.portfolioNames))
            }
            case 'product': {
                return String(R.pathOr('undefined', [R.pathOr(undefined, ['xAxis', index, 'productId'], records)], this.productNames))
            }
            default: {
                return String(R.pathOr(undefined, ['xAxis', index, 'year'], records))
            }
        }
    }

    convertQuadrantCurrency = (element, value, axis, exchangeRateDate) => (
        R.pathOr(false, ['quadrantCurrency', axis], element)
            ? this.convertCurrency(value, exchangeRateDate, element)
            : value
    )

    formatQuadrantAnalysis = (fData, element) => {
        const groupedData = fData.reduce((gd, d) => {
            const curAxisVal = gd[d.multiName] || {}
            const itemYear = d.year >= 1900 ? d.year : new Date(d.endDate).getFullYear()
            const key = d.derivedLabel || this.getItemName(
                element, d.portfolioId, d.productId, d.divisionId, itemYear, undefined, d.tagOptionId,
            )

            return {
                ...gd,
                [d.multiName]: {
                    ...curAxisVal,
                    [key]: {
                        ...d,
                        data: typeof d.data !== 'object'
                            ? this.convertQuadrantCurrency(element, d.data, d.multiName, d.exchangeRateDate)
                            : null,
                    },
                },
            }
        }, {})

        return Object.keys(groupedData.xAxis || {}).map((x) => ({
            x: R.pathOr(null, ['xAxis', x, 'data'], groupedData),
            y: R.pathOr(null, ['yAxis', x, 'data'], groupedData),
            z: R.pathOr(null, ['zAxis', x, 'data'], groupedData),
            name: x,
            series: R.pathOr(null, ['xAxis', x, 'derivedSeries'], groupedData)
                || this.getQuadrantSeriesName(
                    R.pathOr('year', ['widgetRenderSettings', 'colouredBy'], element),
                    groupedData, x,
                ),
        }))
    }

    swapSeriesXAxisData = (unSortedData, xAxis, element) => {
        const { oId } = this.dashStore
        const { sortBy } = this

        const defaultSortKey = oId !== 6 && element.scope === 'product' ? 'endDate' : 'none'
        const sortByKey = R.pathOr(defaultSortKey, ['widgetRenderSettings', 'sortBy'], element)
        const sortDirection = R.pathOr(1, ['widgetRenderSettings', 'sortDirection'], element)
        const data = sortBy(unSortedData, sortByKey, sortDirection)
        const xAxisLabels = data.map((d) => d.name)
        const fData = xAxis.map((x, i) => {
            const newSeries = {
                name: this.removeProductTag(x),
                data: [],
            }
            data.forEach((s) => {
                newSeries.data.push(R.pathOr(s.data, ['data', i], s))
            })
            return newSeries
        })
        return { fData, xAxisLabels }
    }

    // Append each option's reporting value to its axis label as "Option Label (n)".
    // Options are matched to labels by their (html-stripped) option label, so only
    // options that actually have a reporting value are suffixed.
    appendReportingValues = (xAxisLabels, element) => {
        const optionReportingValues = element.optionReportingValues || {}
        if (!Array.isArray(xAxisLabels) || !Object.keys(optionReportingValues).length) return xAxisLabels

        const reportingValueByLabel = (element.options || []).reduce((acc, [optId, label]) => {
            const reportingValue = optionReportingValues[String(optId).toLowerCase()]
            if (reportingValue != null) acc[stripOutHtml(label)] = reportingValue
            return acc
        }, {})

        return xAxisLabels.map((label) => {
            const reportingValue = reportingValueByLabel[label]
            return reportingValue != null ? `${label} (${reportingValue})` : label
        })
    }

    formatData = (filteredData, element, surveyYear) => {
        const { kpiType, dataDetails, chartType } = element
        const { type } = dataDetails

        const removeBaseLimit = element.settings?.removeBaseLimit || 0
        const isOptionLimit = ['SB', 'RD', 'CH'].includes(element.questionTypeCode) && R.isNotNil(removeBaseLimit)
        const isTBLimit = element.questionTypeCode === 'TB' && removeBaseLimit > 0
        const useBaseLimit = isOptionLimit || isTBLimit
        const removedKeys = new Set()
        let limitedData = filteredData
        if (isOptionLimit) {
            const keptKeys = new Set()
            const seenKeys = new Set()
            limitedData = filteredData.map((d) => {
                if (!d?.data || !d?.responses) return d
                const newData = Object.keys(d.data).reduce((acc, k) => {
                    seenKeys.add(k)
                    if ((d.responses[k.toUpperCase()] ?? 0) >= removeBaseLimit) {
                        acc[k] = d.data[k]
                        keptKeys.add(k)
                    }
                    return acc
                }, {})
                return { ...d, data: newData }
            })
            seenKeys.forEach((k) => { if (!keptKeys.has(k)) removedKeys.add(k) })
        } else if (isTBLimit) {
            const rowIds = (element.questionOptions || []).map((o) => String(o.id))
            const keptRows = new Set()
            const seenRows = new Set()
            limitedData = filteredData.map((d) => {
                if (!d?.data || !d?.responses) return d
                const removedThisData = new Set()
                rowIds.forEach((rowId) => {
                    const upper = rowId.toUpperCase()
                    const direct = d.responses[upper] ?? d.responses[rowId]
                    const rowTotal = direct != null
                        ? Number(direct)
                        : Object.keys(d.responses).reduce((sum, key) => {
                            const [keyRowId] = String(key).split('_')
                            if (keyRowId === upper || keyRowId === rowId) {
                                return sum + Number(d.responses[key] || 0)
                            }
                            return sum
                        }, 0)
                    seenRows.add(rowId)
                    if ((rowTotal ?? 0) <= removeBaseLimit) removedThisData.add(rowId)
                    else keptRows.add(rowId)
                })
                if (removedThisData.size === 0) return d
                const newData = Object.keys(d.data).reduce((acc, k) => {
                    const [keyRowId] = String(k).split('_')
                    if (!removedThisData.has(keyRowId)) acc[k] = d.data[k]
                    return acc
                }, {})
                return { ...d, data: newData }
            })
            seenRows.forEach((rowId) => { if (!keptRows.has(rowId)) removedKeys.add(rowId) })
        }

        let fData
        // format incoming data
        if (['TX', 'TA'].includes(element.questionTypeCode)) return { fData: limitedData }
        if (kpiType === 'quadrantAnalysis') fData = limitedData
        if (element.questionTypeCode === 'OD') fData = this.formatPriorityData(limitedData, element)
        else if (element.nonQuestionType === 'KPI') {
            if (['pct'].includes(type)) fData = this.formatPctData(limitedData, element)
            else fData = this.formatAvgData(limitedData, element)
        } else if (element.parentQuestion && !['TB', 'TB - row'].includes(element.questionTypeCode)) fData = this.formatNFOption(limitedData, element)
        else if (['TB', 'TB - row'].includes(element.questionTypeCode)) fData = this.formatTBData(limitedData, element, surveyYear)
        else if ((['avg', 'nps'].includes(kpiType) || element.nonQuestionType) && (kpiType !== 'LMAT')) fData = this.formatAvgData(limitedData, element)
        else if (['pct', 'LMAT'].includes(kpiType)) fData = this.formatPctData(limitedData, element)
        if (!fData || R.isEmpty(fData)) return [] // avoid formating errors if there is no fData at this point
        let xAxisLabels = kpiType === 'OSS' ? [] : this.getXAxisLabels(element, fData) // get axis labels if necessary
        if (useBaseLimit && removedKeys.size > 0 && Array.isArray(xAxisLabels)) {
            const removedLabels = isOptionLimit
                ? (element.options || [])
                    .filter(([k]) => removedKeys.has(k))
                    .map(([, v]) => stripOutHtml(v))
                : (element.questionOptions || [])
                    .filter((o) => removedKeys.has(String(o.id)))
                    .map((o) => stripOutHtml(this.getTBOptionName(o, element)))
            xAxisLabels = xAxisLabels.filter((l) => (
                !removedLabels.some((label) => l === label || l.startsWith(`${label}-`))
            ))
        }
        // format for specific chart
        if (element.questionTypeCode === 'OD') {
            const responses = element.data?.[0]?.responses ?? {}
            xAxisLabels = element.options.map(([k, v]) => `${v} (${responses[k.toUpperCase()] ?? 0})`)
        }
        else if (['npsChart', 'kpiChart', 'column', 'standardColumn', 'standardBar', 'inheritedStackedBar', 'bar', 'line', 'pie', 'doughnut'].includes(chartType)) fData = this.formatBarColumnData(fData, xAxisLabels, element)
        else if (['quadrantAnalysis'].includes(chartType)) fData = this.formatQuadrantAnalysis(limitedData, element)
        else if (['stackedColumn', 'stackedBar', 'stackedLine'].includes(chartType)) fData = this.formatStackedData(fData, element, xAxisLabels)
        else if (['gauge', 'text'].includes(chartType) && element.questionTypeCode === 'TB - row') fData = this.formatGauge(fData, element)
        const swapSeriesXAxis = R.pathOr(false, ['widgetRenderSettings', 'swapSeriesXAxis'], element)
        if (swapSeriesXAxis) return this.swapSeriesXAxisData(fData, xAxisLabels, element)
        return {
            fData: fData || [],
            xAxisLabels: this.appendReportingValues(xAxisLabels, element),
        }
    }
}

export default DataFormatter
